/**
 * React Query hooks for Tenant Management
 * Provides caching and deduplication for tenant API calls
 */

import {
  useQuery,
  useQueries,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import toast from "../../../components/ds/composite/Toast";
import { tenantApi } from "../services";
import type {
  TenantListParams,
  CreateTenantDto,
  UpdateTenantDto,
  TenantStatusValue,
} from "../types";
import { queryKeys } from "../../../api/queryKeys";
import { invalidateTenants } from "../../../api/queryInvalidation";

/** @deprecated Use `queryKeys.tenants` from `../../../api/queryKeys` instead */
export const tenantKeys = queryKeys.tenants;

/**
 * Hook for fetching lightweight tenant list for dropdowns/selectors
 * Uses React Query for caching and deduplication
 * Returns only active tenants with minimal fields: tenantId, name, logo
 */
export const useTenantsListDropdown = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: queryKeys.tenants.listDropdown(),
    queryFn: () => tenantApi.getTenantsList(),
    staleTime: 5 * 60 * 1000, // 5 minutes - tenant list doesn't change often
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled: options?.enabled !== false,
  });
};

/**
 * Hook for fetching paginated tenant list with filtering and sorting
 * Uses React Query for caching and deduplication
 */
export const useTenantsList = (
  params: TenantListParams,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: queryKeys.tenants.list(params),
    queryFn: () => tenantApi.getTenants(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled: options?.enabled !== false,
  });
};

/**
 * Hook for fetching tenant categories from the API
 * Used in Create/Edit Tenant forms for the category dropdown
 */
export const useTenantCategories = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: queryKeys.tenants.categories(),
    queryFn: () => tenantApi.getTenantCategories(),
    staleTime: 10 * 60 * 1000, // 10 minutes - categories rarely change
    gcTime: 30 * 60 * 1000, // 30 minutes
    enabled: options?.enabled !== false,
  });
};

/**
 * Hook for resolving a tenant's logo to a short-lived presigned URL.
 * Results are cached for 10 minutes to avoid N+1 requests in lists/dropdowns.
 */
export const useTenantLogoUrl = (tenantId: number, hasLogo: boolean) =>
  useQuery({
    queryKey: queryKeys.tenants.logoUrl(tenantId),
    queryFn: () => tenantApi.getTenantLogoUrl(tenantId),
    enabled: hasLogo && tenantId > 0,
    staleTime: 10 * 60 * 1000, // presigned URLs are typically valid for 15 min
    gcTime: 15 * 60 * 1000,
  });

/**
 * Batch hook for resolving multiple tenant logo URLs at once.
 * Uses useQueries to parallelise requests and avoid N+1 calls in list views.
 * Returns a Map<tenantId, logoUrl> for O(1) lookup.
 */
export const useTenantLogoUrls = (
  tenants: Array<{ id: number; hasLogo: boolean }> | null | undefined,
): Map<number, string | undefined> => {
  const safeTenants = (tenants ?? []).filter((t) => t.hasLogo && t.id > 0);
  return useQueries({
    queries: safeTenants.map((t) => ({
      queryKey: queryKeys.tenants.logoUrl(t.id),
      queryFn: () => tenantApi.getTenantLogoUrl(t.id),
      staleTime: 10 * 60 * 1000,
      gcTime: 15 * 60 * 1000,
    })),
    combine: (results) => {
      const map = new Map<number, string | undefined>();
      safeTenants.forEach((t, i) => {
        map.set(t.id, results[i]?.data);
      });
      return map;
    },
  });
};

/**
 * Hook for fetching a single tenant by ID
 */
export const useTenantById = (
  tenantId: number,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: queryKeys.tenants.detail(tenantId),
    queryFn: () => tenantApi.getTenantById(tenantId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000,
    enabled: tenantId > 0 && options?.enabled !== false,
  });
};

/**
 * Hook for creating a new tenant
 */
export const useCreateTenant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      data,
      logoFile,
    }: {
      data: CreateTenantDto;
      logoFile?: File | null;
    }) => tenantApi.createTenant(data, logoFile),
    onSuccess: () => {
      invalidateTenants(queryClient);
    },
  });
};

/**
 * Hook for updating a tenant
 */
export const useUpdateTenant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
      logoFile,
    }: {
      id: number;
      data: UpdateTenantDto;
      logoFile?: File | null;
    }) => tenantApi.updateTenant(id, data, logoFile),
    onSuccess: (_data, { id }) => {
      invalidateTenants(queryClient);
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenants.logoUrl(id),
      });
    },
  });
};

/**
 * Hook for updating tenant status
 */
export const useUpdateTenantStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: TenantStatusValue }) =>
      tenantApi.updateTenantStatus(id, { status }),
    onSuccess: () => {
      invalidateTenants(queryClient);
    },
  });
};

/**
 * Hook for activating a tenant
 */
export const useActivateTenant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => tenantApi.activateTenant(id),
    onSuccess: () => {
      invalidateTenants(queryClient);
    },
  });
};

/**
 * Hook for deactivating a tenant
 */
export const useDeactivateTenant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => tenantApi.deactivateTenant(id),
    onSuccess: () => {
      invalidateTenants(queryClient);
    },
  });
};

/**
 * Hook for suspending a tenant
 */
export const useSuspendTenant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => tenantApi.suspendTenant(id),
    onSuccess: () => {
      invalidateTenants(queryClient);
    },
  });
};

/**
 * Hook for fetching subcategories assigned to a tenant
 */
export const useTenantSubcategories = (
  tenantId: number,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: queryKeys.tenants.tenantSubcategories(tenantId),
    queryFn: () => tenantApi.getTenantSubcategories(tenantId),
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: tenantId > 0 && options?.enabled !== false,
  });
};

/**
 * Hook for fetching subcategories available to add to a tenant
 */
export const useAvailableSubcategories = (
  tenantId: number,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: queryKeys.tenants.availableSubcategories(tenantId),
    queryFn: () => tenantApi.getAvailableSubcategories(tenantId),
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: tenantId > 0 && options?.enabled !== false,
  });
};

/**
 * Hook for adding subcategories to a tenant
 */
export const useAddTenantSubcategories = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      subcategoryIds,
    }: {
      tenantId: number;
      subcategoryIds: number[];
    }) => tenantApi.addTenantSubcategories(tenantId, subcategoryIds),
    onSuccess: (_data, { tenantId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenants.tenantSubcategories(tenantId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenants.availableSubcategories(tenantId),
      });
      toast.success("Subcategories added successfully");
    },
    onError: () => {
      toast.error("Failed to add subcategories");
    },
  });
};

/**
 * Hook for removing subcategories from a tenant
 */
export const useRemoveTenantSubcategories = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      subcategoryIds,
    }: {
      tenantId: number;
      subcategoryIds: number[];
    }) => tenantApi.removeTenantSubcategories(tenantId, subcategoryIds),
    onSuccess: (_data, { tenantId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenants.tenantSubcategories(tenantId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenants.availableSubcategories(tenantId),
      });
      toast.success("Subcategory removed successfully");
    },
    onError: () => {
      toast.error("Failed to remove subcategory");
    },
  });
};

/**
 * Hook for deleting a tenant
 */
export const useDeleteTenant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => tenantApi.deleteTenant(id),
    onSuccess: () => {
      invalidateTenants(queryClient);
    },
  });
};

/**
 * Hook for triggering on-demand compliance profile generation.
 * Invalidates the tenant detail cache so the caller re-fetches updated profileData.
 */
export const useGenerateTenantProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tenantId: number) => tenantApi.generateProfile(tenantId),
    onSuccess: (_data, tenantId) => {
      // Keeps React Query cache in sync for any consumer of useTenantById.
      // TenantDetailPage uses direct state; its refresh is driven by onProfileGenerated().
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenants.detail(tenantId),
      });
    },
  });
};
